import { z } from 'zod'
import { uuidSchema } from './uuid.validator.js'

/**
 * The secret in a link the API sends out: an invite, a proposal sent to a
 * client, or an app's request to connect.
 *
 * Every one is minted as `randomBytes(32).toString('base64url')`: 43
 * characters of the URL-safe alphabet, with no padding and no dot. The API
 * keeps only a hash, so the shape is all a page can check; whether the token
 * opens anything is the server's lookup.
 */
export const linkTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/)

/**
 * A route or query value that is a link token, or null when it is missing or
 * not one. As with `parseUuid`, a failed parse is "not found", not a request.
 */
export function parseLinkToken(value: string | null | undefined): string | null {
  const parsed = linkTokenSchema.safeParse(value)
  return parsed.success ? parsed.data : null
}

const RENDER_GRANT_PREFIX = 'pdf'

/**
 * The short-lived pass the API's PDF printer opens a proposal on, in place of
 * a client's link token: `pdf.<versionId>.<expiry>.<signature>`, as
 * `ProposalRenderGrantService.mint` writes it. The expiry is whole seconds,
 * and the signature is an HMAC-SHA256 in base64url, which is a link token's
 * shape.
 *
 * The shape only. Whether the signature is right and the grant unexpired is
 * the server's check, and stays there.
 */
export const renderGrantSchema = z.string().refine((value) => {
  const [prefix, versionId, expiry, signature, ...rest] = value.split('.')
  return (
    rest.length === 0 &&
    prefix === RENDER_GRANT_PREFIX &&
    uuidSchema.safeParse(versionId).success &&
    /^\d+$/.test(expiry ?? '') &&
    linkTokenSchema.safeParse(signature).success
  )
})

/** A route value that is a render grant, or null when it is missing or not one. */
export function parseRenderGrant(value: string | null | undefined): string | null {
  const parsed = renderGrantSchema.safeParse(value)
  return parsed.success ? parsed.data : null
}
